import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { resolveDeadlineFormState } from "@lms/backend/lib/cohorts/deadline-form-state";
import { useCohortsVersion } from "@/components/instructor/cohorts-changed";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Cohort = { id: string; name: string };
type AssignmentOption = {
  assignmentId: string;
  lessonTitle: string;
  moduleTitle: string;
  dueAt: string | null;
  allowLate: boolean;
};
type DeadlineRow = {
  id: string;
  cohortId: string;
  cohortName: string;
  assignmentId: string;
  lessonTitle: string;
  dueAt: string | null;
  allowLate: boolean;
};

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function applyFormState(
  nextCohortId: string,
  nextAssignmentId: string,
  nextAssignments: AssignmentOption[],
  nextDeadlines: DeadlineRow[],
) {
  const lesson = nextAssignments.find((row) => row.assignmentId === nextAssignmentId);
  if (!lesson) {
    return { dueAt: "", allowLate: false, clearDue: false, hasOverride: false };
  }
  const overrideRow = nextDeadlines.find(
    (row) => row.cohortId === nextCohortId && row.assignmentId === nextAssignmentId,
  );
  const state = resolveDeadlineFormState({
    lessonDefault: {
      dueAt: lesson.dueAt,
      allowLate: lesson.allowLate,
    },
    override: overrideRow ? { dueAt: overrideRow.dueAt, allowLate: overrideRow.allowLate } : null,
  });
  return {
    dueAt: state.clearDue ? "" : toDatetimeLocalValue(state.dueAt),
    allowLate: state.allowLate,
    clearDue: state.clearDue,
    hasOverride: state.hasOverride,
  };
}

type Props = { courseId: string };

export function CourseCohortDeadlinesPanel({ courseId }: Props) {
  const t = useTranslations("instructor");
  // C4 (CRS-16): server errors are shown by code, never as the server's English.
  const tRoot = useTranslations();
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [assignments, setAssignments] = useState<AssignmentOption[]>([]);
  const [deadlines, setDeadlines] = useState<DeadlineRow[]>([]);
  const [cohortId, setCohortId] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [allowLate, setAllowLate] = useState(false);
  const [clearDue, setClearDue] = useState(false);
  const [stashedDueAt, setStashedDueAt] = useState("");
  const [hasOverride, setHasOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const cohortsVersion = useCohortsVersion(courseId);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [cohortRes, deadlineRes] = await Promise.all([
        apiFetch(`/v1/instructor/courses/${courseId}/cohorts`),
        apiFetch(`/v1/instructor/courses/${courseId}/cohort-deadlines`),
      ]);
      if (!active) return;

      const cohortJson = await cohortRes.json();
      const deadlineJson = await deadlineRes.json();
      const nextCohorts = cohortRes.ok ? (cohortJson.data.cohorts as Cohort[]) : [];
      const nextAssignments = deadlineRes.ok
        ? (deadlineJson.data.assignments as AssignmentOption[])
        : [];
      const nextDeadlines = deadlineRes.ok ? (deadlineJson.data.deadlines as DeadlineRow[]) : [];

      setCohorts(nextCohorts);
      setAssignments(nextAssignments);
      setDeadlines(nextDeadlines);

      setCohortId((currentCohort) => {
        // A deleted cohort can't stay selected.
        const resolvedCohort = nextCohorts.some((row) => row.id === currentCohort)
          ? currentCohort
          : nextCohorts[0]?.id || "";
        setAssignmentId((currentAssignment) => {
          const resolvedAssignment = currentAssignment || nextAssignments[0]?.assignmentId || "";
          const form = applyFormState(
            resolvedCohort,
            resolvedAssignment,
            nextAssignments,
            nextDeadlines,
          );
          setDueAt(form.dueAt);
          setAllowLate(form.allowLate);
          setClearDue(form.clearDue);
          setStashedDueAt(form.dueAt);
          setHasOverride(form.hasOverride);
          return resolvedAssignment;
        });
        return resolvedCohort;
      });
    })();
    return () => {
      active = false;
    };
  }, [courseId, reloadToken, cohortsVersion]);

  function hydrateSelection(
    nextCohortId: string,
    nextAssignmentId: string,
    nextAssignments = assignments,
    nextDeadlines = deadlines,
  ) {
    const form = applyFormState(nextCohortId, nextAssignmentId, nextAssignments, nextDeadlines);
    setDueAt(form.dueAt);
    setAllowLate(form.allowLate);
    setClearDue(form.clearDue);
    setStashedDueAt(form.dueAt);
    setHasOverride(form.hasOverride);
  }

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!clearDue && !dueAt) {
        setError(t("cohortDeadlineDueRequired"));
        return;
      }
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohort-deadlines`, {
        method: "PUT",
        body: JSON.stringify(
          clearDue
            ? { cohortId, assignmentId, clearDue: true, allowLate }
            : {
                cohortId,
                assignmentId,
                dueAt: new Date(dueAt).toISOString(),
                allowLate,
              },
        ),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortDeadlineSaveFailed"));
        return;
      }
      setReloadToken((value) => value + 1);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveOverride() {
    setRemoving(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohort-deadlines`, {
        method: "DELETE",
        body: JSON.stringify({ cohortId, assignmentId }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortDeadlineRemoveFailed"));
        return;
      }
      setReloadToken((value) => value + 1);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <section data-testid="course-cohort-deadlines-panel">
      <h2 className="text-lg font-medium">{t("cohortDeadlinesTitle")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("cohortDeadlinesHint")}</p>

      {assignments.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--text-muted)]">{t("cohortDeadlinesNoAssignments")}</p>
      ) : (
        <form className="mt-4 space-y-3" onSubmit={(event) => void handleSave(event)}>
          <label className="block text-sm">
            <span className="text-[var(--text-muted)]">{t("cohortEventCohort")}</span>
            <select
              className="mt-1 w-full rounded border border-[var(--border-strong)] px-3 py-2"
              value={cohortId}
              onChange={(event) => {
                const next = event.target.value;
                setCohortId(next);
                hydrateSelection(next, assignmentId);
              }}
              data-testid="cohort-deadline-cohort"
            >
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-[var(--text-muted)]">{t("cohortDeadlineAssignment")}</span>
            <select
              className="mt-1 w-full rounded border border-[var(--border-strong)] px-3 py-2"
              value={assignmentId}
              onChange={(event) => {
                const next = event.target.value;
                setAssignmentId(next);
                hydrateSelection(cohortId, next);
              }}
              data-testid="cohort-deadline-assignment"
            >
              {assignments.map((row) => (
                <option key={row.assignmentId} value={row.assignmentId}>
                  {row.moduleTitle} — {row.lessonTitle}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={clearDue}
              onChange={(event) => {
                const next = event.target.checked;
                if (next) {
                  setStashedDueAt(dueAt);
                  setDueAt("");
                } else {
                  setDueAt(stashedDueAt);
                }
                setClearDue(next);
              }}
              data-testid="cohort-deadline-clear-due"
            />
            <span>{t("cohortDeadlineClearDue")}</span>
          </label>
          <label className="block text-sm">
            <span className="text-[var(--text-muted)]">{t("cohortDeadlineDueAt")}</span>
            <input
              type="datetime-local"
              step={1}
              className="mt-1 w-full rounded border border-[var(--border-strong)] px-3 py-2 disabled:opacity-50"
              value={dueAt}
              disabled={clearDue}
              onChange={(event) => {
                setDueAt(event.target.value);
                setStashedDueAt(event.target.value);
              }}
              data-testid="cohort-deadline-due-at"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowLate}
              onChange={(event) => setAllowLate(event.target.checked)}
              data-testid="cohort-deadline-allow-late"
            />
            <span>{t("cohortDeadlineAllowLate")}</span>
          </label>
          {hasOverride ? (
            <p
              className="text-xs text-[var(--text-muted)]"
              data-testid="cohort-deadline-override-badge"
            >
              {t("cohortDeadlineOverrideActive")}
            </p>
          ) : (
            <p
              className="text-xs text-[var(--text-muted)]"
              data-testid="cohort-deadline-lesson-default-badge"
            >
              {t("cohortDeadlineUsingLessonDefault")}
            </p>
          )}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={saving || !cohortId || !assignmentId || (!clearDue && !dueAt)}
              className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm disabled:opacity-50"
              data-testid="cohort-deadline-save"
            >
              {saving ? t("cohortDeadlineSaving") : t("cohortDeadlineSave")}
            </button>
            {hasOverride ? (
              <button
                type="button"
                disabled={removing || !cohortId || !assignmentId}
                onClick={() => void handleRemoveOverride()}
                className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm disabled:opacity-50"
                data-testid="cohort-deadline-remove"
              >
                {removing ? t("cohortDeadlineRemoving") : t("cohortDeadlineRemove")}
              </button>
            ) : null}
          </div>
        </form>
      )}

      <ul className="mt-6 space-y-2" data-testid="course-cohort-deadlines-list">
        {deadlines.length === 0 ? (
          <li className="text-sm text-[var(--text-muted)]">{t("cohortDeadlinesEmpty")}</li>
        ) : (
          deadlines.map((row) => (
            <li
              key={row.id}
              className="rounded border border-[var(--border)] px-3 py-2 text-sm"
              data-testid={`cohort-deadline-${row.id}`}
            >
              <span className="font-medium">{row.cohortName}</span>
              {" — "}
              {row.lessonTitle}
              {": "}
              {row.dueAt ? new Date(row.dueAt).toLocaleString() : t("cohortDeadlineNoDue")}
              {row.allowLate ? ` (${t("cohortDeadlineAllowLate")})` : ""}
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
